import { useMutation } from '@apollo/client'
import { Editor } from '@tiptap/react'
import { ApolloError, isApolloError } from '@apollo/client'
import { SendTestBlast } from 'recruiter/graphql/rosters.gql'
import useToast from 'shared/components/Toast/useToast'
import { formatGraphQLError } from 'shared/utils'
import { FormValues } from './blastValidationSchema'
import {
    makeMergeFieldRemover,
    makeMergeFieldExtractor,
    MergeFieldData,
} from './makeMergeUtils'

export const useSendTestEmail = (
    editor: Editor | null | undefined,
    mergeFieldData: MergeFieldData | undefined,
    formValues: FormValues
) => {
    const toast = useToast()
    const [sendTestBlastMutation] = useMutation(SendTestBlast)

    const removeMergeFields = makeMergeFieldRemover(mergeFieldData)
    const extractUsedMergeFields = makeMergeFieldExtractor(mergeFieldData)

    return async () => {
        const htmlBody = editor?.getHTML() || ''
        const rawBody = editor?.getText() || ''
        const contentHtml = removeMergeFields(htmlBody)
        const mergeFieldsPresent = extractUsedMergeFields(contentHtml)
        const isEmailBodyEmpty =
            editor?.isEmpty && mergeFieldsPresent.length === 0

        let errors = [
            !formValues.subject ? 'Subject is required' : '',
            isEmailBodyEmpty ? 'Email body is empty' : '',
        ]

        if (errors.some(Boolean)) {
            const errorsText = errors.filter((i) => i !== '').join(', ')
            toast({
                title: 'Error',
                severity: 'error',
                message: `Please fix the following: ${errorsText}`,
            })
            return
        }

        try {
            await sendTestBlastMutation({
                variables: {
                    subject: formValues.subject,
                    contentRaw: rawBody,
                    contentHtml,
                },
                onCompleted: (data: any) => {
                    toast({
                        title: 'Test Email Sent!',
                        severity: 'success',
                        message:
                            data.sendTestBlast?.message ??
                            'Test email sent to your inbox',
                    })
                },
                onError: (error: any) => {
                    console.error(error)
                    toast({
                        title: 'Error!',
                        severity: 'error',
                        message:
                            error?.message ??
                            'Failed to send test email. Please try again.',
                    })
                },
            })
        } catch (e) {
            if (isApolloError(e as Error)) {
                toast({
                    title: 'Error',
                    message: formatGraphQLError(
                        e as ApolloError,
                        'Failed to send test email.'
                    ),
                    severity: 'error',
                })
            } else {
                toast({
                    title: 'Error',
                    message: 'Failed to send test email.',
                    severity: 'error',
                })
            }
        }
    }
}
